/**
 * Batch Genealogy — All Deviations panel.
 * Campaign-wide "Batches with deviations & failures". Each entry: stage chip +
 * batch no., "IN THIS LINEAGE" tag when applicable, status, deviation no.+type
 * OR analytical-failure count, "View batch detail" + a QMS deviation link.
 */
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CallMadeIcon from '@material-ui/icons/CallMade';
import { fetchCampaignDeviations } from '../api';

const useStyles = makeStyles(() => ({
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 1400 },
  panel: {
    position: 'fixed', top: 0, right: 0, height: '100vh', width: 470, maxWidth: '92vw', background: '#fff',
    boxShadow: '-8px 0 24px rgba(15,23,42,0.18)', zIndex: 1401, display: 'flex', flexDirection: 'column',
  },
  head: { padding: '16px 20px', borderBottom: '1px solid #eef2f7' },
  campaign: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: '#b91c1c', textTransform: 'uppercase' },
  title: { fontSize: 20, fontWeight: 800, color: '#000000', marginTop: 6 },
  close: { position: 'absolute', top: 16, right: 20, cursor: 'pointer', color: '#000000', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4, background: '#fff', display: 'inline-flex', fontFamily: 'inherit' },
  body: { padding: '14px 20px 28px', overflowY: 'auto', flex: 1 },
  card: { borderLeft: '3px solid #f59e0b', border: '1px solid #eef2f7', borderRadius: 10, padding: 14, marginBottom: 12 },
  cardFail: { borderLeft: '3px solid #dc2626' },
  row1: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  stepChip: { fontSize: 12, fontWeight: 800, color: '#000000', background: '#eef2f7', borderRadius: 6, padding: '2px 8px' },
  batch: { fontSize: 14, fontWeight: 800, color: '#333333' },
  lineageTag: { fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: '#3730a3', background: '#e0e7ff', borderRadius: 6, padding: '2px 7px', textTransform: 'uppercase' },
  status: { marginLeft: 'auto', fontSize: 12, fontWeight: 800, borderRadius: 10, padding: '2px 9px', textTransform: 'capitalize' },
  detail: { fontSize: 13, color: '#333333', margin: '9px 0 12px' },
  actions: { display: 'flex', gap: 8 },
  viewBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, color: '#000000', cursor: 'pointer', fontFamily: 'inherit' },
  qmsBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 800, color: '#6d28d9', cursor: 'pointer', fontFamily: 'inherit' },
  loading: { padding: 40, textAlign: 'center', color: '#333333', fontSize: 14 },
}));

const statusStyle = (status) => {
  if (/fail/i.test(status)) return { background: '#fee2e2', color: '#b91c1c' };
  if (/closed/i.test(status)) return { background: '#ede9fe', color: '#6d28d9' };
  return { background: '#fef3c7', color: '#b45309' }; // under investigation
};

const AllDeviationsDrawer = ({ campaignId, lineageBatchIds, onClose, onViewBatch, onOpenQMS }) => {
  const classes = useStyles();
  const [deviations, setDeviations] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchCampaignDeviations({ campaignId, lineageBatchIds }).then((res) => {
      if (alive) setDeviations(res.data.deviations);
    });
    return () => { alive = false; };
  }, [campaignId, lineageBatchIds]);

  return (
    <>
      <div className={classes.overlay} onClick={onClose} />
      <div className={classes.panel} role="dialog" aria-label="Batches with deviations and failures">
        <div className={classes.head}>
          <span className={classes.campaign}>⚑ Campaign {campaignId}</span>
          <div className={classes.title}>Batches with deviations &amp; failures</div>
          <button type="button" className={classes.close} onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></button>
        </div>

        <div className={classes.body}>
          {!deviations ? (
            <div className={classes.loading}>Loading deviations…</div>
          ) : (
            deviations.map((d) => (
              <div key={`${d.batch}-${d.deviationNumber || d.kind}`} className={`${classes.card} ${d.kind === 'testfail' ? classes.cardFail : ''}`}>
                <div className={classes.row1}>
                  <span className={classes.stepChip}>Step {d.stage}</span>
                  <span className={classes.batch}>{d.batch}</span>
                  {d.inLineage && <span className={classes.lineageTag}>In this lineage</span>}
                  <span className={classes.status} style={statusStyle(d.status)}>{d.status}</span>
                </div>
                <div className={classes.detail}>
                  {d.kind === 'deviation'
                    ? `${d.deviationNumber} · ${d.deviationType}`
                    : `${d.failures} analytical test failure(s)`}
                </div>
                <div className={classes.actions}>
                  <button type="button" className={classes.viewBtn} onClick={() => onViewBatch(d.batch)}>
                    View batch detail ›
                  </button>
                  {d.kind === 'deviation' && (
                    <button type="button" className={classes.qmsBtn} onClick={() => onOpenQMS && onOpenQMS(d)}>
                      {d.deviationNumber} <CallMadeIcon style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default AllDeviationsDrawer;
